import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Palmtree, Loader2, X, Paperclip, Download, BarChart3 } from 'lucide-react';
import { YearSummary } from './YearSummary';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

const TYPE_LABEL = { ferie: '🌴 Ferie', permesso: '🕐 Permesso', malattia: '🤒 Malattia', assenza: '📅 Assente' };
const today = () => new Date().toLocaleDateString('sv-SE');
const fmtD = (d) => new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });

const inputCls = 'w-full bg-slate-700 border border-slate-600 rounded-lg h-10 px-2 text-sm text-white';

export const uploadCertificate = async (absenceId, files, inpsProtocol) => {
  const fd = new FormData();
  (files || []).forEach(f => fd.append('files', f));
  if (inpsProtocol) fd.append('inps_protocol', inpsProtocol);
  const r = await axios.post(`${API}/hr/absences/${absenceId}/certificate`, fd, { headers: authHeaders() });
  return r.data;
};

export const openCertificate = async (absenceId, fileId) => {
  const r = await axios.get(`${API}/hr/absences/${absenceId}/certificate/${fileId}/download`, { headers: authHeaders() });
  window.open(r.data.download_url, '_blank');
};

// Pannello certificato medico per una malattia del dipendente (tema scuro)
const CertPanel = ({ absence, onDone }) => {
  const [files, setFiles] = useState([]);
  const [prot, setProt] = useState(absence.inps_protocol || '');
  const [busy, setBusy] = useState(false);
  const certs = absence.certificates || [];
  const submit = async () => {
    if (!files.length && !prot.trim()) { toast.error('Allega un file o indica il protocollo INPS'); return; }
    setBusy(true);
    try {
      const r = await uploadCertificate(absence.id, files, prot.trim());
      toast.success(r.email_sent ? 'Certificato inviato alla segreteria ✅' : 'Certificato salvato ✅');
      setFiles([]);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore caricamento certificato'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 border border-slate-600 rounded-lg p-3 space-y-2 bg-slate-700/40" data-testid="hr-cert-panel">
      <p className="text-xs font-semibold text-slate-300">📎 Certificato medico</p>
      {certs.length > 0 && (
        <div className="space-y-1">
          {certs.map(c => (
            <button key={c.id} data-testid={`hr-cert-download-${c.id}`} onClick={() => openCertificate(absence.id, c.id).catch(() => toast.error('Errore download'))}
              className="flex items-center gap-1.5 text-xs text-sky-300 hover:text-sky-200 underline">
              <Download className="h-3 w-3" /> {c.file_name}
            </button>
          ))}
        </div>
      )}
      {absence.inps_protocol && <p className="text-xs text-slate-400">Protocollo INPS: <b className="text-slate-200">{absence.inps_protocol}</b></p>}
      <input type="file" multiple data-testid="hr-cert-files-input" className="w-full text-xs text-slate-300"
        onChange={e => setFiles(Array.from(e.target.files || []))} />
      <input data-testid="hr-cert-inps-input" className={inputCls} placeholder="N. protocollo INPS (facoltativo)"
        value={prot} onChange={e => setProt(e.target.value)} />
      <button data-testid="hr-cert-upload-btn" disabled={busy} onClick={submit}
        className="w-full h-9 bg-sky-600 hover:bg-sky-500 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Carica e invia alla segreteria'}
      </button>
      <p className="text-[10px] text-slate-500">Il certificato viene inviato automaticamente via email a segreteria@cedelabora.it e all'amministrazione.</p>
    </div>
  );
};

// Sezione Ferie/Permessi per il dipendente (pagina Timbratura, tema scuro)
export const AbsenceSection = ({ onChanged }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: 'ferie', date_from: today(), date_to: today(), time_from: '09:00', time_to: '13:00', note: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [certOpenId, setCertOpenId] = useState(null);
  const [newCertFiles, setNewCertFiles] = useState([]);
  const [newCertProt, setNewCertProt] = useState('');
  const [showYear, setShowYear] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/hr/absences?date_from=${today()}`, { headers: authHeaders() });
      setItems(r.data.absences || []);
    } catch { /* modulo non attivo o non censito */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSaving(true); setError('');
    try {
      const payload = { type: form.type, date_from: form.date_from, note: form.note };
      if (form.type === 'permesso') Object.assign(payload, { date_to: form.date_from, time_from: form.time_from, time_to: form.time_to });
      else payload.date_to = form.date_to;
      const r = await axios.post(`${API}/hr/absences`, payload, { headers: authHeaders() });
      toast.success('Richiesta registrata ✅');
      if (form.type === 'malattia' && (newCertFiles.length || newCertProt.trim()) && r.data?.absence?.id) {
        try {
          const cr = await uploadCertificate(r.data.absence.id, newCertFiles, newCertProt.trim());
          toast.success(cr.email_sent ? 'Certificato inviato alla segreteria ✅' : 'Certificato salvato ✅');
        } catch (ce) {
          toast.error(ce?.response?.data?.detail || 'Malattia registrata ma caricamento certificato fallito: riprova dal 📎');
        }
      }
      setOpen(false); setForm({ ...form, note: '' });
      setNewCertFiles([]); setNewCertProt('');
      load(); onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Errore nella richiesta');
    } finally { setSaving(false); }
  };

  const cancel = async (a) => {
    if (!window.confirm('Annullare questa assenza? I saldi verranno restituiti.')) return;
    try {
      await axios.delete(`${API}/hr/absences/${a.id}`, { headers: authHeaders() });
      toast.success('Assenza annullata, saldi restituiti');
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const mine = items.filter(a => a.mine);
  const team = items.filter(a => !a.mine);

  return (
    <div className="bg-slate-800 rounded-2xl p-5 space-y-4" data-testid="hr-absence-section">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-300 flex items-center gap-2"><Palmtree className="h-4 w-4" /> Ferie e Permessi</p>
        <button data-testid="hr-abs-open-btn" onClick={() => { setOpen(!open); setError(''); }}
          className="text-xs bg-emerald-600 hover:bg-emerald-500 rounded-lg px-3 py-1.5 font-semibold">
          {open ? 'Chiudi' : '+ Richiedi'}
        </button>
      </div>

      {open && (
        <div className="space-y-3 border border-slate-700 rounded-xl p-4" data-testid="hr-abs-form">
          <div className="flex gap-2">
            {['ferie', 'permesso', 'malattia'].map(t => (
              <button key={t} data-testid={`hr-abs-type-${t}`} onClick={() => { setForm({ ...form, type: t }); setError(''); }}
                className={`flex-1 rounded-lg py-2 text-sm font-semibold border ${form.type === t ? 'bg-emerald-600 border-emerald-500' : 'bg-slate-700 border-slate-600 text-slate-300'}`}>
                {TYPE_LABEL[t]}
              </button>
            ))}
          </div>
          {form.type === 'permesso' ? (
            <div className="grid grid-cols-3 gap-2">
              <div><p className="text-xs text-slate-400 mb-1">Giorno</p>
                <input type="date" data-testid="hr-abs-date-from" className={inputCls} value={form.date_from} onChange={e => setForm({ ...form, date_from: e.target.value })} /></div>
              <div><p className="text-xs text-slate-400 mb-1">Dalle</p>
                <input type="time" data-testid="hr-abs-time-from" className={inputCls} value={form.time_from} onChange={e => setForm({ ...form, time_from: e.target.value })} /></div>
              <div><p className="text-xs text-slate-400 mb-1">Alle</p>
                <input type="time" data-testid="hr-abs-time-to" className={inputCls} value={form.time_to} onChange={e => setForm({ ...form, time_to: e.target.value })} /></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div><p className="text-xs text-slate-400 mb-1">Dal</p>
                <input type="date" data-testid="hr-abs-date-from" className={inputCls} value={form.date_from} onChange={e => setForm({ ...form, date_from: e.target.value })} /></div>
              <div><p className="text-xs text-slate-400 mb-1">Al</p>
                <input type="date" data-testid="hr-abs-date-to" className={inputCls} value={form.date_to} onChange={e => setForm({ ...form, date_to: e.target.value })} /></div>
            </div>
          )}
          <input data-testid="hr-abs-note" className={inputCls} placeholder="Nota (facoltativa)" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          {form.type === 'malattia' && (
            <div className="space-y-2 border border-slate-600 rounded-lg p-3 bg-slate-700/40" data-testid="hr-abs-new-cert">
              <p className="text-xs text-slate-400">📎 Certificato medico (facoltativo — puoi aggiungerlo anche dopo)</p>
              <input type="file" multiple data-testid="hr-abs-new-cert-files" className="w-full text-xs text-slate-300"
                onChange={e => setNewCertFiles(Array.from(e.target.files || []))} />
              <input data-testid="hr-abs-new-cert-inps" className={inputCls} placeholder="N. protocollo INPS (facoltativo)"
                value={newCertProt} onChange={e => setNewCertProt(e.target.value)} />
            </div>
          )}
          {error && <p className="text-sm text-rose-400 bg-rose-950/40 border border-rose-800 rounded-lg p-3" data-testid="hr-abs-error">{error}</p>}
          <button data-testid="hr-abs-submit-btn" disabled={saving} onClick={submit}
            className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 rounded-xl font-bold flex items-center justify-center gap-2">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Conferma richiesta'}
          </button>
          <p className="text-[11px] text-slate-500">La richiesta è immediata: i saldi vengono scalati subito. Il customer service deve garantire sempre almeno 2 presenti.</p>
        </div>
      )}

      <div>
        <p className="text-xs text-slate-500 mb-2">Le mie prossime assenze</p>
        {mine.length === 0 ? <p className="text-sm text-slate-500" data-testid="hr-abs-mine-empty">Nessuna assenza in programma</p> : (
          <div className="space-y-1.5" data-testid="hr-abs-mine-list">
            {mine.map(a => (
              <div key={a.id}>
                <div className="flex items-center justify-between text-sm bg-slate-700/50 rounded-lg px-3 py-2">
                  <span>{TYPE_LABEL[a.type] || a.type} · {fmtD(a.date_from)}{a.date_to !== a.date_from ? `–${fmtD(a.date_to)}` : ''}
                    {a.time_from ? ` ${a.time_from}-${a.time_to}` : ''}
                    <span className="text-slate-500 ml-1">{a.days_count ? `(${a.days_count} gg)` : a.hours_count ? `(${a.hours_count} h)` : ''}</span>
                    {a.type === 'malattia' && (a.certificates || []).length > 0 && <span className="text-sky-400 text-xs ml-1">📎{a.certificates.length}</span>}
                  </span>
                  <span className="flex items-center gap-2">
                    {a.type === 'malattia' && (
                      <button data-testid={`hr-cert-toggle-${a.id}`} title="Certificato medico"
                        onClick={() => setCertOpenId(certOpenId === a.id ? null : a.id)}
                        className="text-sky-400 hover:text-sky-300"><Paperclip className="h-4 w-4" /></button>
                    )}
                    <button data-testid={`hr-abs-cancel-${a.id}`} title="Annulla" onClick={() => cancel(a)} className="text-rose-400 hover:text-rose-300"><X className="h-4 w-4" /></button>
                  </span>
                </div>
                {certOpenId === a.id && <CertPanel absence={a} onDone={load} />}
              </div>
            ))}
          </div>
        )}
      </div>

      {team.length > 0 && (
        <div>
          <p className="text-xs text-slate-500 mb-2">Assenze del team</p>
          <div className="space-y-1" data-testid="hr-abs-team-list">
            {team.map(a => (
              <p key={a.id} className="text-sm text-slate-400">
                <b className="text-slate-300">{a.employee_name}</b> · assente {fmtD(a.date_from)}{a.date_to !== a.date_from ? `–${fmtD(a.date_to)}` : ''}{a.time_from ? ` (${a.time_from}-${a.time_to})` : ''}
              </p>
            ))}
          </div>
        </div>
      )}

      <div>
        <button data-testid="hr-year-toggle" onClick={() => setShowYear(!showYear)}
          className="w-full flex items-center justify-center gap-2 text-xs text-slate-400 hover:text-slate-200 border border-slate-700 rounded-lg py-2">
          <BarChart3 className="h-3.5 w-3.5" /> {showYear ? 'Nascondi riepilogo annuale' : 'Riepilogo annuale'}
        </button>
        {showYear && <YearSummary />}
      </div>
    </div>
  );
};
